import {
	DMSCategory,
	DMSSubCategory,
	DMSSection,
	dmsGetCategoryDetail,
	DMSCategoryDetailResponse,
} from '@/libs/fleet-api/services/dms';
import { Card, Collapse, Empty, Spin } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const transformCategoryDetailResponse = (
	response: DMSCategoryDetailResponse,
	categoryId: string,
): DMSSubCategory[] => {
	const subCategories: DMSSubCategory[] = [];
	const normalizedId = categoryId.toLowerCase();

	// Handle financing-approval-document
	if (normalizedId === 'financing-approval-document') {
		const categoryData = response.financingApprovalDocument;
		if (!categoryData?.details || categoryData.details.length === 0) {
			return [];
		}

		categoryData.details.forEach((detail) => {
			const section: DMSSection = {
				id: 'default',
				label: 'Documents',
				documents: Array.isArray(detail.documents) ? detail.documents : [],
			};

			// Store both mklNo and prospectId in composite id format: mklNo|prospectId
			const compositeId =
				detail.mklNo && detail.prospectId
					? `${detail.mklNo}|${detail.prospectId}`
					: detail.mklNo || detail.prospectId;

			const subCategory: DMSSubCategory = {
				id: compositeId,
				type: categoryId,
				label: detail.mklNo || `${detail.customerName} (${detail.prospectId})`,
				sections: [section],
			};

			subCategories.push(subCategory);
		});

		return subCategories;
	}

	// Handle covenant-document
	if (normalizedId === 'covenant-document') {
		const categoryData = response.covenantDocument;
		if (!categoryData?.details || categoryData.details.length === 0) {
			return [];
		}

		categoryData.details.forEach((detail, index) => {
			const sections: DMSSection[] = [];

			if (Array.isArray(detail.spfList) && detail.spfList.length > 0) {
				detail.spfList.forEach((spf) => {
					const section: DMSSection = {
						id: `spf-${spf.spfId}`,
						label: `${spf.spfNo}|${spf.spfStatus}`,
						documents: Array.isArray(spf.documents) ? spf.documents : [],
					};
					sections.push(section);
				});
			}

			if (sections.length > 0) {
				// Store both mklNo and prospectId in composite id format: mklNo|prospectId
				const compositeId =
					detail.mklNo && detail.prospectId
						? `${detail.mklNo}|${detail.prospectId}`
						: detail.mklNo || detail.prospectId;

				const subCategory: DMSSubCategory = {
					id: compositeId,
					type: categoryId,
					label: detail.mklNo || `${index + 1}`,
					sections,
				};

				subCategories.push(subCategory);
			}
		});

		return subCategories;
	}

	// Handle financing-disbursement-document
	if (normalizedId === 'financing-disbursement-document') {
		const categoryData = response.financingDisbursementDocument;
		if (!categoryData?.mklList || categoryData.mklList.length === 0) {
			return [];
		}

		categoryData.mklList.forEach((mkl) => {
			const sections: DMSSection[] = [];

			if (Array.isArray(mkl.mppList) && mkl.mppList.length > 0) {
				mkl.mppList.forEach((mpp) => {
					// Create sections for disbursement, PO, and Agreement documents
					const disbursementRelatedSections: DMSSection[] = [];

					// Disbursement sections
					if (
						Array.isArray(mpp.disbursementList) &&
						mpp.disbursementList.length > 0
					) {
						mpp.disbursementList.forEach((disbursement) => {
							// Disbursement documents
							if (
								Array.isArray(disbursement.documents) &&
								disbursement.documents.length > 0
							) {
								disbursementRelatedSections.push({
									id: `disbursement-${disbursement.disbursementId}`,
									label: `Disbursement ${disbursement.disbursementNo} (${disbursement.scheme})`,
									documents: disbursement.documents,
									metadata: { scheme: disbursement.scheme },
								});
							}

							// Purchase Order sections
							if (
								Array.isArray(disbursement.purchaseOrderList) &&
								disbursement.purchaseOrderList.length > 0
							) {
								disbursement.purchaseOrderList.forEach((po) => {
									// PO documents
									if (Array.isArray(po.documents) && po.documents.length > 0) {
										disbursementRelatedSections.push({
											id: `disbursement-${disbursement.disbursementId}-po-${po.purchaseOrderId}`,
											label: `PO ${po.purchaseOrderNo}`,
											documents: po.documents,
											metadata: {
												type: 'po',
												disbursementId: disbursement.disbursementId,
												poId: po.purchaseOrderId,
											},
										});
									}

									// Agreement sections
									if (
										Array.isArray(po.agreementList) &&
										po.agreementList.length > 0
									) {
										po.agreementList.forEach((agreement) => {
											if (
												Array.isArray(agreement.documents) &&
												agreement.documents.length > 0
											) {
												disbursementRelatedSections.push({
													id: `disbursement-${disbursement.disbursementId}-agreement-${agreement.agreementId}`,
													label: `Agreement ${agreement.agreementNo}`,
													documents: agreement.documents,
													metadata: {
														type: 'agreement',
														disbursementId: disbursement.disbursementId,
														poId: po.purchaseOrderId,
													},
												});
											}
										});
									}
								});
							}
						});
					}

					// Only add MPP selection section if MPP has documents or disbursements
					const hasMppDocuments =
						Array.isArray(mpp.documents) && mpp.documents.length > 0;
					const hasDisbursements =
						Array.isArray(mpp.disbursementList) &&
						mpp.disbursementList.length > 0;

					if (hasMppDocuments || hasDisbursements) {
						// Add MPP selection section (for selection UI)
						// Format: label = "mppNo|agreementNo" to store both values
						sections.push({
							id: `mpp-select-${mpp.mppId}`,
							label: `${mpp.mppNo}|${mpp.agreementNo || ''}`,
							documents: [],
						});

						// Add MPP documents section (for "Dokumen Memo Pencairan Pembiayaan (MPP)" table)
						if (hasMppDocuments) {
							sections.push({
								id: `mpp-docs-${mpp.mppId}`,
								label: `MPP Documents`,
								documents: mpp.documents,
							});
						}

						// Add disbursement selection sections (for "Pilih No. Dokumen Kesepakatan Realisasi Pencairan")
						if (hasDisbursements) {
							mpp.disbursementList.forEach((disbursement) => {
								sections.push({
									id: `disbursement-select-${disbursement.disbursementId}`,
									label: disbursement.disbursementNo,
									documents: [],
								});
							});
						}

						// Add all disbursement-related sections (disbursement, PO, Agreement) with prefix to identify which MPP they belong to
						disbursementRelatedSections.forEach((section) => {
							sections.push({
								...section,
								id: `mpp-${mpp.mppId}-${section.id}`,
							});
						});
					}
				});
			}

			if (sections.length > 0) {
				const subCategory: DMSSubCategory = {
					id: mkl.prospectId,
					type: categoryId,
					label: `${mkl.mklNo}`,
					sections,
				};

				subCategories.push(subCategory);
			}
		});

		return subCategories;
	}

	return [];
};

const DMSCategoryDetail: FC<{
	keyword: string;
	categories: DMSCategory[];
	customerId: string;
	onClick: (subCategory: DMSSubCategory, category: DMSCategory) => void;
}> = ({ keyword, categories, customerId, onClick }) => {
	const [activeKey, setActiveKey] = useState<string[]>([]);
	const [fetchedCategories, setFetchedCategories] = useState<
		Record<string, DMSSubCategory[]>
	>({});

	const handleChange = (keys: string | string[]) => {
		const keysArray = Array.isArray(keys) ? keys : [keys];
		setActiveKey(keysArray);
	};

	const getSubCategories = (category: DMSCategory): DMSSubCategory[] => {
		if (category.fetchType === 'split') {
			return fetchedCategories[category.id] ?? [];
		}
		return category.subCategories;
	};

	const items = categories.map((category) => {
		const subCategories = getSubCategories(category);
		const filteredSubCategories = keyword
			? subCategories.filter((sub) => {
					return sub.label.toLowerCase().includes(keyword.toLowerCase());
				})
			: subCategories;

		const isSplitType = category.fetchType === 'split';
		const isOpen = keyword ? true : activeKey.includes(category.id);
		const needsFetch =
			isSplitType && isOpen && fetchedCategories[category.id] === undefined;

		return {
			key: category.id,
			label: category.label,
			children: (
				<CategoryContent
					category={category}
					customerId={customerId}
					subCategories={filteredSubCategories}
					keyword={keyword}
					needsFetch={needsFetch}
					fetchType={category.fetchType || 'full'}
					categoryId={category.id}
					onFetchComplete={(fetchedSubCategories) => {
						setFetchedCategories((prev) => ({
							...prev,
							[category.id]: fetchedSubCategories,
						}));
					}}
					onClick={onClick}
				/>
			),
		};
	});

	return (
		<Collapse
			activeKey={
				keyword ? categories.map((category) => category.id) : activeKey
			}
			onChange={handleChange}
			items={items}
		/>
	);
};

const CategoryContent: FC<{
	category: DMSCategory;
	customerId: string;
	subCategories: DMSSubCategory[];
	keyword: string;
	needsFetch: boolean;
	fetchType: string;
	categoryId: string;
	onFetchComplete: (subCategories: DMSSubCategory[]) => void;
	onClick: (subCategory: DMSSubCategory, category: DMSCategory) => void;
}> = ({
	category,
	customerId,
	subCategories,
	keyword,
	needsFetch,
	fetchType,
	categoryId,
	onFetchComplete,
	onClick,
}) => {
	const { data, isLoading } = useQuery({
		queryKey: ['dmsGetCategoryDetail', customerId, category.id] as const,
		queryFn: ({ queryKey }) => {
			const [, customerId, categoryId] = queryKey;
			return dmsGetCategoryDetail(customerId, categoryId);
		},
		enabled: needsFetch,
	});

	const transformedSubCategories = useMemo(() => {
		if (!data?.data) {
			return null;
		}
		// Use category.id for matching the exact category IDs
		return transformCategoryDetailResponse(data.data, category.id);
	}, [data?.data, category.id]);

	// fetchType and categoryId are available for conditional UI rendering based on category
	// Currently not used but available for future implementation
	void fetchType;
	void categoryId;

	useEffect(() => {
		if (transformedSubCategories !== null && needsFetch) {
			onFetchComplete(transformedSubCategories);
		}
	}, [transformedSubCategories, needsFetch, onFetchComplete]);

	if (needsFetch && isLoading) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					padding: '40px 0',
				}}
			>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
			{subCategories.length ? (
				subCategories.map((sub) => {
					return (
						<Card
							key={sub.id}
							style={{ cursor: 'pointer' }}
							onClick={() => onClick(sub, category)}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 10,
								}}
							>
								<svg
									width="32"
									height="32"
									viewBox="0 0 32 32"
									fill="none"
								>
									<path
										d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
										fill="#FFA000"
									/>
									<path
										d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
										fill="#FFCA28"
									/>
								</svg>
								<strong>{sub.label}</strong>
							</div>
						</Card>
					);
				})
			) : (
				<Empty
					description={
						keyword ? (
							<>
								Your search <b>{`"${keyword}"`}</b> did not match any documents
								in <b>{category.label}</b>.
							</>
						) : (
							'No Data'
						)
					}
				/>
			)}
		</div>
	);
};

export default DMSCategoryDetail;
